import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight, FaUser, FaFilter, FaSearch, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import AdvancedFilter from './AdvancedFilter';
import { apiService } from '../Config/api.service';
import { OPERATORS_BY_TYPE } from '../Constantes/OperatorByType';
import LoadingBarWithStyles from '../Utils/LoadingBarWithStyles';
import AvatarCircle from '../Utils/AvatarCircle';

const VISIBLE_PAGE_COUNT = 5;
const SKELETON_ROW_COUNT = 8;

const toSearchTerms = (searchQuery) => {
  if (Array.isArray(searchQuery)) return searchQuery;
  return searchQuery ? [searchQuery] : [];
};

const getFirstVisiblePage = (currentPage, totalPages) => {
  if (totalPages <= VISIBLE_PAGE_COUNT || currentPage <= 3) return 1;
  if (currentPage >= totalPages - 2) return totalPages - 4;
  return currentPage - 2;
};

const getPageNumbers = (currentPage, totalPages) => {
  const firstPage = getFirstVisiblePage(currentPage, totalPages);
  return Array.from({ length: Math.min(VISIBLE_PAGE_COUNT, totalPages) }, (_, index) => firstPage + index);
};

const getFilterValueLabel = (filter, field) => {
  if (field?.type !== 'selection' || !Array.isArray(field.selection)) return filter.value;
  const option = field.selection.find(([value]) => value === filter.value);
  return option ? option[1] : filter.value;
};

// Un filtre posé par le programme peut porter un `label` lisible (Yros/Kavero#469) :
// il remplace « champ opérateur valeur », illisible pour un champ technique.
export const getFilterBadgeLabel = (filter, fields) => {
  if (filter.label) return filter.label;
  const field = fields?.find(fl => fl.value === filter.field);
  const label = field?.label || field?.name || filter.field;
  const operator = (field && OPERATORS_BY_TYPE[field.type]?.find(o => o.value === filter.operator)?.label) || filter.operator;
  return `${label} ${operator} ${getFilterValueLabel(filter, field)}`;
};

const isColumn = (column, name) => column.key === name || column === name;

const renderNameWithAvatar = (row, column, path, t) => (
  <div>
    <Link className="font-semibold flex items-center gap-3 text-gray-900 hover:text-black-600 transition-colors duration-200" to={`/${path}/${row.id}${window.location.search}`}>
      <div className="relative flex-shrink-0">
        {row['image'] ? (
          <img src={`data:image/png;base64,${row['image']}`} alt="" className="w-8 h-8 object-cover rounded-full" />
        ) : (
          <AvatarCircle
            name={row[column.key || column]}
            size={32}
          />
        )}
        {row['contract_expiration_pending'] && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-amber-500 border-2 border-white rounded-full"
            title={t('contractExpiration.expiredTitle')}
          />
        )}
      </div>
      {row[column.key || column]}
    </Link>
  </div>
);

const renderImageCell = (value) => (
  value ? (
    <img src={value} alt="" className="w-8 h-8 object-cover rounded-full" />
  ) : (
    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
      <FaUser className="w-4 h-4 text-gray-400" />
    </div>
  )
);

const renderCellContent = (row, column, path, t) => {
  const value = row[column.key || column];
  if (column.render) return column.render(row);
  if (column.key === 'name' && column.avatar === true) return renderNameWithAvatar(row, column, path, t);
  if (isColumn(column, 'Image')) return renderImageCell(value);
  if (isColumn(column, 'Statut')) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {value}
      </span>
    );
  }
  if (path) {
    return (
      <Link
        to={`/${path}/${row.id}${window.location.search}`}
        className="text-black-600 hover:text-black-800 transition-colors duration-200"
      >
        {value}
      </Link>
    );
  }
  return value;
};

const TableSkeleton = ({ columns, selectable, hasExtraActions }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {selectable && (
            <th className="px-6 py-3 w-12">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            </th>
          )}
          {columns.map((_, index) => (
            <th key={index} className="px-6 py-3">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
            </th>
          ))}
          {hasExtraActions && (
            <th className="px-6 py-3">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
            </th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {[...Array(SKELETON_ROW_COUNT)].map((_, idx) => (
          <tr key={idx}>
            {selectable && (
              <td className="px-6 py-4">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </td>
            )}
            {columns.map((_, colIdx) => (
              <td key={colIdx} className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
              </td>
            ))}
            {hasExtraActions && (
              <td className="px-6 py-4">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TableHead = ({ columns, selectable, extraActions, stickyActions, isAllSelected, isIndeterminate, onSelectAll, onSort, sortBy }) => (
  <thead className="bg-gray-50 border-b border-gray-200">
    <tr>
      {selectable && (
        <th className="px-6 py-3 w-12">
          <input
            type="checkbox"
            className="w-4 h-4 text-black-600 border-gray-300 rounded focus:ring-black-500"
            checked={isAllSelected}
            ref={el => {
              if (el) el.indeterminate = isIndeterminate;
            }}
            onChange={e => onSelectAll(e.target.checked)}
          />
        </th>
      )}
      {columns.map((column, index) => (
        <th
          key={index}
          className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
            column.sortable ? 'cursor-pointer select-none hover:bg-gray-100 transition-colors duration-200' : ''
          }`}
          onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
        >
          <div className="flex items-center gap-2">
            {column.label || column}
            {column.sortable && sortBy?.key === column.key && (
              <span className="text-black-500">
                {sortBy.direction === 'asc' ? <FaArrowUp size={12} /> : <FaArrowDown size={12} />}
              </span>
            )}
          </div>
        </th>
      ))}
      {extraActions && (
        <th className={`px-6 py-3 w-20${stickyActions ? ' sticky right-0 z-10 bg-gray-50' : ''}`}></th>
      )}
    </tr>
  </thead>
);

const EmptyRow = ({ colSpan }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center">
        <div className="flex flex-col items-center">
          <div className="bg-gray-100 rounded-full p-3 mb-3">
            <FaSearch className="text-gray-400" size={24} />
          </div>
          <p className="text-lg font-medium text-gray-900 mb-1">{t('tableList.noResultFound')}</p>
          <p className="text-sm text-gray-500">{t('tableList.tryModifyingCriteria')}</p>
        </div>
      </td>
    </tr>
  );
};

const TableBody = ({ data, columns, path, selectable, extraActions, stickyActions, onRowClick, selectedRows, onToggleRow }) => {
  const { t } = useTranslation();
  const colSpan = columns.length + (selectable ? 1 : 0) + (extraActions ? 1 : 0);

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {data.length === 0 ? (
        <EmptyRow colSpan={colSpan} />
      ) : (
        data.map((row, rowIndex) => (
          <tr
            key={rowIndex}
            className={`hover:bg-gray-50 transition-colors duration-200 ${onRowClick ? 'cursor-pointer' : ''}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {selectable && (
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-black-600 border-gray-300 rounded focus:ring-black-500"
                  checked={selectedRows.includes(row.id)}
                  onChange={() => onToggleRow(row.id)}
                />
              </td>
            )}
            {columns.map((column, colIndex) => (
              <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {renderCellContent(row, column, path, t)}
              </td>
            ))}
            {extraActions && (
              <td
                className={`px-6 py-4 text-right text-sm font-medium${
                  stickyActions ? ' sticky right-0 z-10 bg-white shadow-[-8px_0_8px_-8px_rgba(15,23,42,0.18)]' : ''
                }`}
              >
                {typeof extraActions === 'function'
                  ? extraActions(row)
                  : extraActions}
              </td>
            )}
          </tr>
        ))
      )}
    </tbody>
  );
};

const SelectionBanners = ({ selectable, isAllSelected, allPagesSelected, selectedCount, totalItems, onSelectAllPages, onClear }) => {
  const { t } = useTranslation();

  if (selectable && allPagesSelected) {
    return (
      <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-2 flex items-center gap-2">
        {t('tableList.allItemsSelected', { count: selectedCount })}
        <button
          className="ml-auto text-gray-500 hover:text-red-500"
          onClick={onClear}
          type="button"
        >
          {t('tableList.deselectAll')}
        </button>
      </div>
    );
  }
  if (selectable && isAllSelected) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded mb-2 flex items-center gap-2">
        {t('tableList.allPageItemsSelected')}
        <button
          className="underline text-black-600 ml-2"
          onClick={onSelectAllPages}
          type="button"
        >
          {t('tableList.selectAllItems', { count: totalItems || '...' })}
        </button>
        <button
          className="ml-auto text-gray-500 hover:text-red-500"
          onClick={onClear}
          type="button"
        >
          {t('tableList.deselectAll')}
        </button>
      </div>
    );
  }
  return null;
};

const ToolbarExtras = ({ toolbarActions, selectedRows, selectActions }) => {
  const { t } = useTranslation();
  return (
    <>
      {toolbarActions && (
        <div className="flex items-center gap-3 flex-wrap">
          {typeof toolbarActions === 'function' ? toolbarActions() : toolbarActions}
        </div>
      )}

      {/* Actions de sélection */}
      {selectedRows.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {t('tableList.selectedCount', { count: selectedRows.length })}
          </span>
          {typeof selectActions === 'function'
            ? selectActions(selectedRows)
            : selectActions
          }
        </div>
      )}
    </>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  return (
    <div className="bg-white px-6 py-3 border-t border-gray-200">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          {t('employeePayslipNav.page', { current: currentPage, total: totalPages })}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FaArrowLeft size={12} />
            {t('dailySalaryInputs.previous')}
          </button>

          <div className="flex gap-1">
            {getPageNumbers(currentPage, totalPages).map((pageNumber) => (
              <button
                key={pageNumber}
                className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 ${
                  currentPage === pageNumber
                    ? 'bg-black-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => onPageChange(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>

          <button
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t('dailySalaryInputs.next')}
            <FaArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

const TableList = ({
  columns,
  data,
  path,
  searchDisplay = true,
  searchQuery,
  setSearchQuery,
  filterBy,
  setFilterBy,
  showFilter = true,
  onSort,
  sortBy,
  pagination,
  onPageChange,
  onRowClick,
  model,
  selectable = false,
  extraActions,
  onSelectedRowsChange,
  selectActions,
  toolbarActions,
  loading = false,
  // Épingle la colonne d'actions à droite quand le tableau défile en largeur :
  // sans elle, les boutons de la dernière colonne sortent du cadre sur un écran
  // de portable (supervision RH, Yros/Kavero#18). Désactivée par défaut.
  stickyActions = false,
}) => {
  const { t } = useTranslation();
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const currentPage = pagination?.page || localCurrentPage;
  const totalPages = pagination?.total_pages || 1;
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [fields, setFields] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchTerms, setSearchTerms] = useState(() => toSearchTerms(searchQuery));
  const [allPagesSelected, setAllPagesSelected] = useState(false);

  // Memoize all row ids for select all
  const allRowIds = useMemo(() => data.map(row => row.id), [data]);
  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < data.length;

  useEffect(() => {
    if (onSelectedRowsChange) onSelectedRowsChange(selectedRows);
  }, [selectedRows, onSelectedRowsChange]);

  const handlePageChange = useCallback(
    (pageNumber) => {
      if (onPageChange) {
        onPageChange(pageNumber, searchTerms.length ? searchTerms : inputValue, filterBy);
      } else {
        setLocalCurrentPage(pageNumber);
      }
    },
    [onPageChange, searchTerms, inputValue, filterBy]
  );

  const handleCheckboxChange = useCallback(
    (id) => {
      setSelectedRows(prev =>
        prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
      );
    },
    []
  );

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedRows(allRowIds);
      setAllPagesSelected(false);
    } else {
      setSelectedRows([]);
      setAllPagesSelected(false);
    }
  }, [allRowIds]);

  const handleSelectAllPages = async () => {
    // Fetch all IDs from backend (implement an API endpoint for this)
    const res = await apiService.getElement(`/all-ids/${model}`);
    setSelectedRows(res.data.ids);
    setAllPagesSelected(true);
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setAllPagesSelected(false);
  };

  const handleSearchKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        if (!searchTerms.includes(inputValue.trim())) {
          const newTerms = [...searchTerms, inputValue.trim()];
          setSearchTerms(newTerms);
          setSearchQuery(newTerms);
          setInputValue('');
          if (onPageChange) onPageChange(1, newTerms, filterBy);
        }
      }
    },
    [inputValue, searchTerms, setSearchQuery, onPageChange, filterBy]
  );

  const handleRemoveSearchTerm = useCallback(
    (term) => {
      const newTerms = searchTerms.filter(t => t !== term);
      setSearchTerms(newTerms);
      setSearchQuery(newTerms);
      if (onPageChange) onPageChange(1, newTerms, filterBy);
    },
    [searchTerms, setSearchQuery, onPageChange, filterBy]
  );

  const handleShowAdvancedFilter = useCallback(async () => {
    if (!showAdvancedFilter && fields.length === 0 && model) {
      try {
        const res = await apiService.getElement(`/fields/${model}`);
        const data = res.data;
        setFields(
          data.fields.map(f => ({
            label: f.string,
            value: f.name,
            type: f.type,
            relation: f.relation,
            selection: f.selection,
            name: f.name,
          }))
        );
      } catch {
        setFields([]);
      }
    }
    setShowAdvancedFilter(prev => !prev);
  }, [showAdvancedFilter, fields.length, model]);

  const handleRemoveFilter = (index) => {
    const newFilters = filterBy.filters.filter((_, i) => i !== index);
    setFilterBy({ ...filterBy, filters: newFilters });
    if (onPageChange) onPageChange(1, searchTerms, { ...filterBy, filters: newFilters });
  };

  const handleApplyFilter = (filters, includeArchived, globalLogic) => {
    setShowAdvancedFilter(false);
    setFilterBy({ filters, includeArchived, globalLogic });
    if (onPageChange) onPageChange(1, searchTerms, { filters, includeArchived, globalLogic });
  };

  // Badge rendering modernisé
  const renderBadge = (label, onRemove, key) => (
    <span key={key} className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-black-100 text-black-800 rounded-full border border-black-200">
      {label}
      <button
        type="button"
        className="flex items-center justify-center w-4 h-4 text-black-600 hover:text-red-500 hover:bg-red-100 rounded-full transition-colors duration-200"
        onClick={onRemove}
        aria-label={t('hspForm.delete')}
      >
        <FaTimes size={10} />
      </button>
    </span>
  );

  return (
    <div className="space-y-4">
      <div className='bg-white p-2 rounded-lg'>
      {/* Filtres appliqués */}
      {filterBy?.filters?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {filterBy.filters.map((f, idx) =>
            renderBadge(getFilterBadgeLabel(f, fields), () => handleRemoveFilter(idx), idx)
          )}
        </div>
      )}

      {/* Termes de recherche */}
      {searchTerms.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {searchTerms.map((term, idx) =>
            renderBadge(term, () => handleRemoveSearchTerm(term), idx)
          )}
        </div>
      )}

      <SelectionBanners
        selectable={selectable}
        isAllSelected={isAllSelected}
        allPagesSelected={allPagesSelected}
        selectedCount={selectedRows.length}
        totalItems={pagination?.total_items}
        onSelectAllPages={handleSelectAllPages}
        onClear={handleClearSelection}
      />

      {/* Barre de recherche et filtres */}
      {searchDisplay && (
        <div className="flex gap-4 items-center flex-wrap ">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              id="tablelist-search"
              name="tablelist-search"
              placeholder={t('tableList.searchPlaceholder')}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyUp={handleSearchKeyPress}
              className="w-full pl-10 pr-4 py-2 border border-[--color-primary] rounded-lg focus:ring-2 focus:ring-black-500 focus:border-black-500 transition-colors duration-200"
            />
          </div>

          {showFilter && (
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 text-black-600 border border-black-300 rounded-lg hover:bg-black-50 transition-colors duration-200"
                onClick={handleShowAdvancedFilter}
              >
                <FaFilter size={14} />
                {t('tableList.filters')}
              </button>
              {showAdvancedFilter && (
                <div className="absolute right-0 z-50 mt-2 w-[550px]">
                  <AdvancedFilter
                    fields={fields}
                    onApply={handleApplyFilter}
                  />
                </div>
              )}
            </div>
          )}

          <ToolbarExtras
            toolbarActions={toolbarActions}
            selectedRows={selectedRows}
            selectActions={selectActions}
          />
        </div>
      )}

      </div>

      {/* Table */}
      <div className="bg-white rounded-lg overflow-hidden">
        {loading && <LoadingBarWithStyles />}

        {!data?.length > 0 && loading ? (
          <TableSkeleton columns={columns} selectable={selectable} hasExtraActions={Boolean(extraActions)} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <TableHead
                columns={columns}
                selectable={selectable}
                extraActions={extraActions}
                stickyActions={stickyActions}
                isAllSelected={isAllSelected}
                isIndeterminate={isIndeterminate}
                onSelectAll={handleSelectAll}
                onSort={onSort}
                sortBy={sortBy}
              />
              <TableBody
                data={data}
                columns={columns}
                path={path}
                selectable={selectable}
                extraActions={extraActions}
                stickyActions={stickyActions}
                onRowClick={onRowClick}
                selectedRows={selectedRows}
                onToggleRow={handleCheckboxChange}
              />
            </table>
          </div>
        )}

        {/* Pagination */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      </div>
    </div>
  );
};

const extraActionsPropType = PropTypes.oneOfType([PropTypes.func, PropTypes.node]);

TableSkeleton.propTypes = {
  columns: PropTypes.array.isRequired,
  selectable: PropTypes.bool,
  hasExtraActions: PropTypes.bool,
};

TableHead.propTypes = {
  columns: PropTypes.array.isRequired,
  selectable: PropTypes.bool,
  extraActions: extraActionsPropType,
  stickyActions: PropTypes.bool,
  isAllSelected: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  onSelectAll: PropTypes.func.isRequired,
  onSort: PropTypes.func,
  sortBy: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.string }),
};

EmptyRow.propTypes = {
  colSpan: PropTypes.number.isRequired,
};

TableBody.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  path: PropTypes.string,
  selectable: PropTypes.bool,
  extraActions: extraActionsPropType,
  stickyActions: PropTypes.bool,
  onRowClick: PropTypes.func,
  selectedRows: PropTypes.array.isRequired,
  onToggleRow: PropTypes.func.isRequired,
};

SelectionBanners.propTypes = {
  selectable: PropTypes.bool,
  isAllSelected: PropTypes.bool,
  allPagesSelected: PropTypes.bool,
  selectedCount: PropTypes.number.isRequired,
  totalItems: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectAllPages: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

ToolbarExtras.propTypes = {
  toolbarActions: extraActionsPropType,
  selectedRows: PropTypes.array.isRequired,
  selectActions: extraActionsPropType,
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

TableList.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  path: PropTypes.string,
  searchDisplay: PropTypes.bool,
  searchQuery: PropTypes.any,
  setSearchQuery: PropTypes.func,
  filterBy: PropTypes.any,
  setFilterBy: PropTypes.func,
  pagination: PropTypes.object,
  onPageChange: PropTypes.func,
  onRowClick: PropTypes.func,
  model: PropTypes.string,
  selectable: PropTypes.bool,
  extraActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  toolbarActions: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  stickyActions: PropTypes.bool,
};

export default TableList;
